/* eslint-disable no-underscore-dangle */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Button, Badge, Spinner, Card,
} from 'react-bootstrap';
import BillingoService from '../../Services/BillingoService';
import PaymentsService from '../../Services/PaymentsService';
import {
  formatAmount, bookingValue, orderLineReconciliation, deriveDocStatus,
} from '../../Utils/billingoDocuments';
import BillingoDocumentModal from './BillingoDocumentModal';
import BillingoDocumentList from './BillingoDocumentList';
import KeyValueRow from './KeyValueRow';
import AddInvoiceModal from '../../Pages/Orders/Components/AddInvoiceModal';
import { finalInvoiceBlockedReason } from '../../Utils/tripClaims';

// Per-order invoicing card for the Details modal. Surfaces the synergy between
// order lines, Billingo documents and payments:
//   - per-order-line owed / invoiced / paid / outstanding (reconciliation)
//   - the document list, with payment-driven status
//   - create (Billingo) and upload (external PDF) actions
function BillingoInvoicesPanel({ order, partner = null }) {
  const [documents, setDocuments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const orderId = order._id;
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [docs, pays] = await Promise.all([
        BillingoService.listDocumentsForBooking(order),
        PaymentsService.listForBooking(orderId).catch(() => []),
      ]);
      setDocuments(docs);
      setPayments(Array.isArray(pays) ? pays : []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load invoicing data:', err);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
    // Keyed on _id so an unrelated re-render of the parent (new `order` object
    // with identical data) doesn't refetch.
  }, [orderId]);

  useEffect(() => { refresh(); }, [refresh]);

  // The Számla from a Díjbekérő is final: Completed bookings only.
  const finalBlocked = finalInvoiceBlockedReason([order]);

  const recon = orderLineReconciliation(order, documents, payments);
  // Everything received, net of refunds: what landed on the lines plus any
  // overpayment. The document statuses below are measured against this.
  const orderPaidTotal = recon.totals.paid + recon.unappliedPaid;
  const orderValue = bookingValue(order).gross;
  const cur = recon.currency;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <h6 className="mb-0">
          <i className="fa fa-file-invoice me-2" />
          Invoicing
        </h6>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" onClick={() => setShowUpload(true)}>
            <i className="fa fa-upload me-1" />
            Upload external invoice
          </Button>
          <Button size="sm" onClick={() => setShowModal(true)}>
            <i className="fa fa-plus me-1" />
            Create document
          </Button>
        </div>
      </div>

      {loading && (
        <div className="text-muted small">
          <Spinner size="sm" animation="border" className="me-2" />
          Loading…
        </div>
      )}

      {/* Per-line reconciliation: owed vs invoiced vs paid — table on md+, cards on mobile */}
      {recon.rows.length > 0 && (
        <>
          <Table responsive size="sm" className="align-middle mb-3 d-none d-md-table">
            <thead className="table-light">
              <tr>
                <th>Order line</th>
                <th className="text-end">Value</th>
                <th className="text-end">Invoiced</th>
                <th className="text-end">Paid</th>
                <th className="text-end">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {recon.rows.map((r) => (
                <tr key={r.key}>
                  <td>{r.description}</td>
                  <td className="text-end">{formatAmount(r.value, r.currency)}</td>
                  <td className="text-end">
                    {formatAmount(r.invoiced, r.currency)}
                    {r.uninvoiced > 0.005 && (
                      <Badge bg="warning" className="ms-2" title="Not yet invoiced">
                        {formatAmount(r.uninvoiced, r.currency)}
                        {' '}
                        to invoice
                      </Badge>
                    )}
                  </td>
                  <td className="text-end">{formatAmount(r.paid, r.currency)}</td>
                  <td className="text-end">
                    {r.outstanding <= 0.005
                      ? <Badge bg="success">Paid</Badge>
                      : <strong>{formatAmount(r.outstanding, r.currency)}</strong>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="table-light">
              <tr>
                <th>Total</th>
                <th className="text-end">{formatAmount(recon.totals.value, cur)}</th>
                <th className="text-end">{formatAmount(recon.totals.invoiced, cur)}</th>
                <th className="text-end">{formatAmount(recon.totals.paid, cur)}</th>
                <th className="text-end">{formatAmount(recon.totals.outstanding, cur)}</th>
              </tr>
            </tfoot>
          </Table>

          <div className="d-md-none mb-3">
            {recon.rows.map((r) => (
              <Card key={r.key} className="mb-2">
                <Card.Body className="py-2">
                  <div className="fw-semibold mb-1">{r.description}</div>
                  <KeyValueRow label="Value" value={formatAmount(r.value, r.currency)} />
                  <KeyValueRow label="Invoiced" value={formatAmount(r.invoiced, r.currency)} />
                  <KeyValueRow label="Paid" value={formatAmount(r.paid, r.currency)} />
                  <KeyValueRow
                    label="Outstanding"
                    value={r.outstanding <= 0.005
                      ? <Badge bg="success">Paid</Badge>
                      : <strong>{formatAmount(r.outstanding, r.currency)}</strong>}
                  />
                </Card.Body>
              </Card>
            ))}
            <KeyValueRow
              emphasis
              label="Total outstanding"
              value={formatAmount(recon.totals.outstanding, cur)}
            />
          </div>
        </>
      )}

      {/* Money no line could absorb. This used to read "paid but not allocated
          to a specific line" and fired on 970 bookings, most of them simply
          never keyed — the office was told to go and allocate money that was
          already on the booking. Under the one policy an unkeyed payment is
          spent across the lines, so what is left here is a genuine
          overpayment: 128 bookings, 2026-11-01. Keep the wording honest if
          that ever changes again. */}
      {recon.unappliedPaid > 0.005 && (
        <div className="small text-muted mb-2">
          <i className="fa fa-exclamation-triangle me-1" />
          {formatAmount(recon.unappliedPaid, cur)}
          {' '}
          received beyond what these order lines owe — the order is overpaid.
        </div>
      )}

      {/* Documents */}
      {!loading && documents.length === 0 && (
        <div className="text-muted small">
          No documents issued yet. Use
          {' '}
          <strong>Create document</strong>
          {' '}
          to issue a Díjbekérő, Előlegszámla or Számla — or
          {' '}
          <strong>Upload external invoice</strong>
          {' '}
          to attach a PDF.
        </div>
      )}

      <BillingoDocumentList
        documents={documents.map((doc) => ({
          ...doc, status: deriveDocStatus(doc, orderPaidTotal, orderValue),
        }))}
        finalBlocked={finalBlocked}
        defaultEmail={order.email_address || ''}
        onChanged={refresh}
      />

      <BillingoDocumentModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onComplete={refresh}
        context={{
          mode: 'order',
          order,
          partner: partner || order.partner,
          existingDocs: documents,
          payments,
        }}
      />

      {showUpload && (
        <AddInvoiceModal
          originalOrder={order}
          show={showUpload}
          handleClose={() => { setShowUpload(false); refresh(); }}
        />
      )}
    </>
  );
}

export default BillingoInvoicesPanel;
